import { Navigate, Outlet } from 'react-router';
import { usePermissionStatus } from '../../hooks/useHasPermission';
import { PERMISSION_STATUSES } from '../../store/permissions/permissionsSlice';
import type { Permission } from '../../constants/permissions';
import ROUTES from '../../routes/routeConstants';

interface PermissionRouteGuardProps {
  // The permission the routes below require. Its scope decides how the question
  // is asked — account-wide, or at the route's own :locationId — so a guard
  // cannot check a location permission tenant-wide by mistake.
  permission: Permission;
}

/**
 * Keeps a subtree behind one permission, mirroring the permission the backend
 * requires on the endpoints those pages call.
 *
 * Renders nothing while the answer is unknown rather than redirecting: bouncing
 * a user who turns out to be allowed in would send them home mid-navigation
 * every time the permission fetch is slower than the route change.
 */
export function PermissionRouteGuard({ permission }: Readonly<PermissionRouteGuardProps>) {
  const status = usePermissionStatus(permission);

  if (status === PERMISSION_STATUSES.loading) return null;

  if (status === PERMISSION_STATUSES.denied) {
    return <Navigate to={`/${ROUTES.locations}`} replace />;
  }

  return <Outlet />;
}
